import type {EcommerceSalesChannel} from '@/types/salesChannels';
import type {CscartSalesChannel} from '@/types/salesChannels/cscartSalesChannels';
import type {
  CreateSalesChannelRequest,
  PostRequestDelegationResponse,
  RenameSalesChannelRequest,
} from '@myparcel-frontend/cscart-ecommerce-service';
import type {DelegationApiKeyCredentials, PostRegisterApiKeyResponse} from '@myparcel-frontend/delegation-service';
import {useCscartEcommerceService} from '@myparcel-frontend/cscart-ecommerce-service';
import {useDelegationService} from '@myparcel-frontend/delegation-service';
import {attemptAsync} from 'es-toolkit';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import logToMonitor from '@/utils/logging';

export default function useCscartEcommerceSalesChannels() {
  const salesChannelStore = useSalesChannelStore();

  const getCscartSalesChannels = async (shopIds: string[]): Promise<CscartSalesChannel[] | []> => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.getSalesChannels(shopIds));

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching CS-Cart sales channels failed in useCscart composable',
      });

      throw error;
    }

    const result: CscartSalesChannel[] = [];

    if (!response || !response?.results?.length) {
      return result;
    }

    for (const salesChannel of response.results) {
      const cscartSalesChannel: CscartSalesChannel = {
        ...salesChannel,
        ecommercePlatform: 'CSCART',
      };

      const newSalesChannel = salesChannelStore.addSalesChannel(cscartSalesChannel);

      result.push(newSalesChannel);
    }

    return result;
  };

  const createCscartSalesChannel = async (data: CreateSalesChannelRequest) => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.createSalesChannel(data));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Creating CS-Cart sales channel failed in useCscart composable',
        _myparcel_sales_channel_payload: data,
      });

      throw error;
    }

    const newCscartSalesChannel: CscartSalesChannel = {
      ...response,
      ecommercePlatform: 'CSCART',
    };

    salesChannelStore.addSalesChannel(newCscartSalesChannel);

    return response;
  };

  const renameSalesChannel = async (data: RenameSalesChannelRequest) => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.renameSalesChannel(data));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Renaming CS-Cart sales channel failed in useCscart composable',
        _myparcel_sales_channel_id: data.salesChannelId,
        _myparcel_new_name: data.name,
      });

      throw error;
    }

    const updatedCscartSalesChannel: CscartSalesChannel = {
      ...response,
      ecommercePlatform: 'CSCART',
    };

    salesChannelStore.updateSalesChannel(updatedCscartSalesChannel);

    return response;
  };

  const removeCscartSalesChannel = async (salesChannelId: string) => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.removeSalesChannel(salesChannelId));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Removing CS-Cart sales channel failed in useCscart composable',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }

    salesChannelStore.removeSalesChannel(salesChannelId);

    return response;
  };

  const enableOrderLoading = async (salesChannelId: string) => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.enableOrderLoading(salesChannelId));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Enabling order loading for CS-Cart sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }

    salesChannelStore.enableOrderLoading(salesChannelId);

    return response;
  };

  const disableOrderLoading = async (salesChannelId: string) => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.disableOrderLoading(salesChannelId));

    if (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Disabling order loading for CS-Cart sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      return undefined;
    }

    salesChannelStore.disableOrderLoading(salesChannelId);

    return response;
  };

  const fetchCscartSalesChannelById = async (salesChannelId: string): Promise<CscartSalesChannel | undefined> => {
    const client = useCscartEcommerceService();

    const [error, response] = await attemptAsync(() => client.getSalesChannelById(salesChannelId));

    if (error) {
      logToMonitor(error, {
        _myparcel_extra_info: 'Fetching CS-Cart sales channel by id failed',
        _myparcel_sales_channel: salesChannelId,
      });

      throw error;
    }

    const cscartSalesChannel: CscartSalesChannel = {
      ...response,
      ecommercePlatform: 'CSCART',
    };

    salesChannelStore.addSalesChannel(cscartSalesChannel);

    return cscartSalesChannel;
  };

  const requestDelegation = async (salesChannelId: string): Promise<PostRequestDelegationResponse | undefined> => {
    const client = useCscartEcommerceService();

    const response = await client.requestDelegation(salesChannelId);

    salesChannelStore.updateSalesChannelToDelegationRequested(response.id, {
      authorizeUrl: response.authorizeUrl,
    });

    return response;
  };

  const createApiKeyCredentials = async (
    salesChannelId: EcommerceSalesChannel['id'],
    credentials: DelegationApiKeyCredentials,
  ): Promise<PostRegisterApiKeyResponse> => {
    const client = useDelegationService();

    const result = await client.createDelegationRequestsApiKey({
      apiKey: credentials,
      delegateeId: {
        delegatee: 'CSCART',
        id: salesChannelId,
      },
    });

    return result;
  };

  return {
    getCscartSalesChannels,
    createCscartSalesChannel,
    removeCscartSalesChannel,
    renameSalesChannel,
    enableOrderLoading,
    disableOrderLoading,
    requestDelegation,
    createApiKeyCredentials,
    fetchCscartSalesChannelById,
  };
}
